/**
 * GET /rest/my-tasks  vs  GET /jaxrs/my-tasks
 *
 * The field map is only the exceptions: identical names pair automatically (see
 * compare.js#buildFieldMap), so 49 hand-written entries collapse to the dozen places where the two
 * APIs genuinely disagree.
 */
export const myTasks = {
    id: "my-tasks",
    label: "My tasks",
    summary: "All open tasks assigned to the logged-in user.",
    params: [
        {
            name: "companyId",
            label: "companyId",
            in: "query",
            placeholder: "e.g. 1234",
            appliesTo: ["task", "web"],
            options: "companies",
            help: "Restrict to one company/tenant. Sent to both APIs. Pick by name - the company's id is what goes on the query string.",
        },
        {
            name: "taskUids",
            label: "taskUids",
            in: "query",
            placeholder: "comma-separated task_ uids",
            appliesTo: ["web"],
            options: "tasks",
            help: "Web API only - filters to specific task uids. The list follows the selected company.",
        },
    ],
    sides: {
        task: {
            method: "GET",
            path: "/my-tasks",
            operationId: "getTasks",
            scope: "task:list-own",
            // the spec marks Accept required: it selects the response content version
            accept: "application/vnd.approval-platform-v1+json",
            query: params => ({companyId: params.companyId}),
            extract: body => (Array.isArray(body) ? body : []),
            shape: "PublishedTask[]",
        },
        web: {
            method: "GET",
            path: "/my-tasks",
            operationId: "getAllTasksForUser",
            scope: "task:list-own",
            query: params => ({companyId: params.companyId, taskUids: params.taskUids}),
            extract: body => (Array.isArray(body?.tasks) ? body.tasks : []),
            shape: "TaskListDTO { tasks, hiddenTasksForCurrentSession }",
            // approval-web calls this one with IGNORE_VISMA_CONTEXTID, so no Visma-ContextId header
            sendsContextIdInApp: false,
        },
    },
    compare: {
        key: {task: "uid", web: "uid"},
        /*
         * Two of these are not guessable from the names and were confirmed against live staging
         * data: `externalDocumentId` carries the user-facing document number that the Web API calls
         * `displayId` (the Web API's own `externalId` is a different, internal id), and the Web API
         * returns the document uid twice.
         */
        aliases: [
            {task: "taskId", web: "id"},
            {task: "processId", web: "idProcess"},
            {task: "commentCount", web: "numberOfComments"},
            {task: "attachmentCollection", web: "attachmentsCollection"},
            {task: "externalDocumentId", web: "displayId", note: "same document number under different names"},
            {task: "actions", web: "taskActions", note: "same set, order is not stable between the two"},
            {task: "uidDocument", web: "documentUid", note: "the Web API returns the document uid twice, as uidDocument and documentUid"},
        ],
        // the Task API nests the requester; the Web API flattens it into requester* fields
        derived: [
            {task: "requester.fullName", web: "requesterName", taskRoot: "requester", getTask: item => item.requester?.fullName},
            {task: "requester.userId", web: "requesterId", taskRoot: "requester", getTask: item => item.requester?.userId},
            {task: "requester.firstName", web: "requesterFirstName", taskRoot: "requester", getTask: item => item.requester?.firstName},
            {task: "requester.lastName", web: "requesterLastName", taskRoot: "requester", getTask: item => item.requester?.lastName},
            {task: "requester.email", web: "requesterEmail", taskRoot: "requester", getTask: item => item.requester?.email},
        ],
        notes: {
            dueDate: "spec types differ: string vs int64 epoch millis",
            dueDateWarning: "spec types differ: string vs int64 epoch millis",
            lastChanged: "spec types differ: string vs int64 epoch millis",
            activatedDate: "the Task API truncates this to a date; the Web API keeps the time",
        },
    },
};
